/**
 * LimitOffsetPanel — renders LIMIT / OFFSET / FETCH / TOP values as ONE line
 * headed by the clause's own keyword (`limitClauseKeyword`): `TOP [20]` on SQL
 * Server, `FETCH FIRST [10] rows` on Oracle, `LIMIT [20] OFFSET [5]` elsewhere —
 * so the heading never contradicts the row. A row whose label repeats the
 * heading shows only its value. When `editContext` is supplied, each value is an
 * inline numeric editor (`LimitValueInline`) wired to the `modify-limit` SR-Edit
 * op; without it, values render as non-editable spans (existing scaffolding).
 * With no limit yet, edit-ready surfaces get the same one-line keyword row as
 * the other empty clauses (`TOP — none · [+ Add limit]`), headed by the keyword
 * the engine's "Add limit" writes.
 */

import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import type { ParseOptions } from '../../../../sql-grammar/model';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { modifyLimit } from '../../edit/edits/modifyLimit';
import { useVqeStore } from '../../store/vqeStore';
import { vqeCopy } from '../../ux/vqeCopy';
import { AddButton } from '../common/AddButton';
import { VqeTooltip } from '../common/VqeTooltip';
import { ClauseCard, EMPTY_CLAUSE_LABEL } from './ClauseCard';
import { limitClauseKeyword, PLAIN_FETCH_FIRST } from './limitClauseKeyword';
import type { ClauseSpan, SelectOutline } from './extractSelectOutline';
import { LimitValueInline } from './LimitValueInline';

/** Default row cap seeded by the empty-state "+ Add limit" affordance; the row
 *  then renders and is inline-editable to any value via `LimitValueInline`. */
const ADD_LIMIT_DEFAULT = '100';

export interface LimitOffsetPanelProps {
  readonly bufferText: string;
  readonly outline: SelectOutline;
  readonly options: ParseOptions;
  readonly editContext?: VqeEditContext;
  /** Render nothing for an empty clause — the SELECT's clause add row offers it. */
  readonly hideEmpty?: boolean;
}

/**
 * "+ Add limit": seeds a default row limit via the `modify-limit` edit —
 * dialect-correct per engine (mssql: `TOP (n)`). Shared by the empty LIMIT row
 * and the SELECT's clause add row.
 */
export function LimitAddButton({
  editContext,
  engine,
  label = vqeCopy.actions.addLimit,
}: {
  readonly editContext: VqeEditContext;
  readonly engine: ParseOptions['engine'];
  readonly label?: string;
}) {
  const { track } = useAnalytics();
  const onAddLimit = (): void => {
    void modifyLimit({ count: ADD_LIMIT_DEFAULT }, toCommitCtx(editContext)).catch((error: unknown) => {
      useVqeStore.getState().recordCommitFeedback({
        kind: 'failure',
        at: Date.now(),
        message: error instanceof Error ? error.message : String(error),
      });
    });
    track('feature:vqe_add_limit', { engine });
  };
  return <AddButton label={label} testId="vqe-limit-add" disabled={false} onClick={onAddLimit} />;
}

interface Row {
  readonly label: string;
  readonly testId: string;
  readonly value: string;
  readonly role: 'count' | 'offset';
}

function valueFromSpan(bufferText: string, span: ClauseSpan | null): string {
  if (!span) return '';
  return bufferText.slice(span.start, span.end).trim();
}

function buildRows(bufferText: string, outline: SelectOutline): Row[] {
  const rows: Row[] = [];
  if (outline.top) {
    rows.push({ label: 'TOP', testId: 'vqe-limit-top', value: valueFromSpan(bufferText, outline.top), role: 'count' });
  }
  if (outline.limit) {
    rows.push({ label: 'LIMIT', testId: 'vqe-limit-limit', value: valueFromSpan(bufferText, outline.limit), role: 'count' });
  }
  if (outline.offset) {
    rows.push({ label: 'OFFSET', testId: 'vqe-limit-offset', value: valueFromSpan(bufferText, outline.offset), role: 'offset' });
  }
  if (outline.fetch) {
    rows.push({ label: 'FETCH', testId: 'vqe-limit-fetch', value: valueFromSpan(bufferText, outline.fetch), role: 'count' });
  }
  return rows;
}

/** First run of digits in `text`, or `undefined` when absent/empty. */
function firstDigits(text: string | undefined): string | undefined {
  if (!text) return undefined;
  return (text.match(/\d+/) ?? [null])[0] ?? undefined;
}

export function LimitOffsetPanel({ bufferText, outline, options, editContext, hideEmpty }: LimitOffsetPanelProps) {
  const rows = buildRows(bufferText, outline);
  const keyword = limitClauseKeyword(outline, options.engine, bufferText);

  // No TOP/LIMIT/OFFSET/FETCH yet. Read-only scaffolding (no editContext)
  // renders nothing as before; edit-ready surfaces get a discoverable, named
  // Limit card with a "+ Add limit" affordance (SEL-08 "nowhere to click" /
  // SEL-11 "name it") — unless the SELECT's clause add row offers it instead.
  if (rows.length === 0) {
    if (!editContext || hideEmpty) return null;
    return (
      <ClauseCard
        title={keyword}
        testId="vqe-limit-section"
        density="compact"
        footer={<LimitAddButton editContext={editContext} engine={options.engine} />}
      >
        <VqeTooltip content={vqeCopy.empty.noLimit} selfProvider>
          <span className="text-xs italic text-muted-foreground" data-testid="vqe-limit-empty">
            {EMPTY_CLAUSE_LABEL}
          </span>
        </VqeTooltip>
      </ClauseCard>
    );
  }

  // Derived once per render — threaded into whichever role ISN'T being edited
  // so a single-field edit doesn't wipe out the other part of the clause.
  const topRow = rows.find((r) => r.label === 'TOP');
  const limitRow = rows.find((r) => r.label === 'LIMIT');
  const offsetRow = rows.find((r) => r.label === 'OFFSET');
  const fetchRow = rows.find((r) => r.label === 'FETCH');
  const currentCount = topRow?.value ?? limitRow?.value ?? firstDigits(fetchRow?.value);
  const currentOffset = firstDigits(offsetRow?.value);

  return (
    <ClauseCard title={keyword} testId="vqe-limit-section" density="compact">
      <dl className="flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="vqe-limit-rows">
        {rows.map((row) => {
          // The heading already says TOP / LIMIT / FETCH FIRST — the matching
          // row shows only its value (a plain FETCH tail shrinks to its count).
          const fetchCount =
            keyword === 'Fetch first' && row.testId === 'vqe-limit-fetch'
              ? row.value.match(PLAIN_FETCH_FIRST)?.[1]
              : undefined;
          const shown = fetchCount !== undefined ? { ...row, value: fetchCount } : row;
          const labelIsHeading =
            fetchCount !== undefined || row.label.toLowerCase() === keyword.toLowerCase();
          return (
            <div key={row.testId} className="flex items-baseline gap-1.5" data-testid={row.testId}>
              <dt
                className={
                  labelIsHeading
                    ? 'sr-only'
                    : 'text-[10px] font-semibold uppercase tracking-wide text-muted-foreground'
                }
              >
                {row.label}
              </dt>
              {editContext ? (
                <LimitValueInline
                  row={shown}
                  editContext={editContext}
                  currentCount={currentCount}
                  currentOffset={currentOffset}
                />
              ) : (
                <dd
                  className="cursor-text font-mono text-xs text-foreground"
                  data-testid={`${row.testId}-value`}
                  aria-disabled
                >
                  {shown.value || '—'}
                </dd>
              )}
              {fetchCount !== undefined ? (
                <span className="text-xs text-muted-foreground">rows</span>
              ) : null}
            </div>
          );
        })}
      </dl>
    </ClauseCard>
  );
}
